import React, { useMemo } from "react";
import { Form, Select, Typography } from "antd";
import { useTranslation } from "react-i18next";
import CustomerSelect, { customerName } from "../../../commons/Billing/CustomerSelect";
import { applyChange, linkOptions } from "./links";

const label = doc => [doc.number, doc.title].filter(Boolean).join(" · ") || doc.name || "—";

const docOptions = list =>
  list.map(doc => ({
    value: String(doc._id),
    label: label(doc),
    search: label(doc).toLowerCase()
  }));

const filterOption = (input, option) => option.search.includes(input.trim().toLowerCase());

/**
 * Customer (with branch), project, offer and invoice of an entry. Once a customer is chosen the
 * other dropdowns list only that customer's documents (and an offer or invoice of the chosen
 * project); picking a document first fills in its customer.
 *
 * value: { client, clientBranch, project, offer, invoice } (ids or ""); onChange(nextValue).
 */
const LinkFields = ({ catalog, value, onChange, disabled }) => {
  const { t } = useTranslation("dashboard");
  const options = useMemo(() => linkOptions(value, catalog), [value, catalog]);
  const customer = (catalog?.clients || []).find(c => String(c._id) === value.client);

  const change = (field, next) => onChange(applyChange(value, field, next || "", catalog));

  const select = (field, list, placeholder, empty) => (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      value={value[field] || undefined}
      placeholder={placeholder}
      options={docOptions(list)}
      filterOption={filterOption}
      notFoundContent={<Typography.Text type="secondary">{empty}</Typography.Text>}
      onChange={next => change(field, next)}
    />
  );

  const forCustomer = Boolean(value.client);

  return (
    <Form layout="vertical" component="div" className="sd-links">
      <Form.Item
        label={t("Customer")}
        extra={
          forCustomer
            ? t("Only projects, offers and invoices of {{name}} are listed below.", {
                name: customerName(customer) || t("this customer")
              })
            : t("Choose the customer first to see only their projects, offers and invoices.")
        }
      >
        <CustomerSelect
          customers={catalog?.clients || []}
          value={value.client || undefined}
          branch={value.clientBranch || null}
          disabled={disabled}
          placeholder={t("Choose a customer")}
          describe={c => [c.zipCode, c.city].filter(Boolean).join(" ")}
          onChange={(clientId, branchId) =>
            onChange({
              ...applyChange(value, "client", clientId || "", catalog),
              clientBranch: branchId || ""
            })
          }
        />
      </Form.Item>
      <Form.Item label={t("Project")}>
        {select(
          "project",
          options.projects,
          forCustomer ? t("Choose a project") : t("All projects"),
          forCustomer ? t("This customer has no projects.") : t("No projects yet.")
        )}
      </Form.Item>
      <Form.Item label={t("Offer")}>
        {select(
          "offer",
          options.offers,
          forCustomer ? t("Choose an offer") : t("All offers"),
          forCustomer ? t("This customer has no matching offers.") : t("No offers yet.")
        )}
      </Form.Item>
      <Form.Item label={t("Invoice")}>
        {select(
          "invoice",
          options.invoices,
          forCustomer ? t("Choose an invoice") : t("All invoices"),
          forCustomer ? t("This customer has no matching invoices.") : t("No invoices yet.")
        )}
      </Form.Item>
    </Form>
  );
};

export default LinkFields;
